import React, { forwardRef, Fragment, useMemo, } from 'react';
import classNames from 'classnames';
import { CnIcon } from '@/components/cn-icon';
// import { CnSafeArea } from '@/components/cn-safe-area';
import { CnButton } from '@/components/cn-button';
import { CnModal } from '@/components/cn-modal';
import { useI18n, withI18n } from 'panda-i18n';
import locale from '@/locale';
import { isValidArray } from '@/utils/func';
import isBoolean from 'lodash/isBoolean';
import { mergeProps } from '@/utils/with-default-props';
import isString from 'lodash/isString';
// TODO size mapping
const FooterWrapper = (wrapperProps) => {
    const { centered, children, ...others } = wrapperProps;
    if (centered) {
        return React.createElement("div", { ...others }, children);
    }
    return React.createElement(Fragment, null, children);
};
const MESSAGE_TYPE = {
    alert: 'help',
    confirm: '',
    success: 'complete-r-fill',
    error: 'error-r-fill',
    warning: 'exclamation-t-fill',
    notice: 'exclamation-r-fill',
    help: 'exclamation-r-fill',
};
const defaultProps = {
    visible: false,
    type: 'normal',
    size: 'large',
    footer: true,
    centered: false,
    platform: 'ios',
};
const CnDialogBase = (props, ref) => {
    const $i18n = useI18n();
    const { prefix = 'cn-ui-m-', title: titleProp, content, type, size, visible, contentClassName, bgImg = '', platform, closeMode: closeModeProp, footerActions: footerActionsProp, footer, centered, okProps = { text: '', children: '' }, cancelProps = { text: '', children: '' }, className, showIcon: showIconProp, children, showBottomCloseIcon, onOk = () => { }, onCancel = () => { }, onClose = () => { }, ...others } = mergeProps(defaultProps, props);
    const showIcon = showIconProp === true ? true : !centered;
    const closeMode = useMemo(() => {
        if (closeModeProp) {
            return closeModeProp;
        }
        return centered
            ? ['ok', 'mask', 'cancel']
            : ['close', 'ok', 'mask', 'cancel'];
    }, [closeModeProp, centered]);
    const footerActions = useMemo(() => {
        if (footerActionsProp) {
            return footerActionsProp;
        }
        if (['alert', 'success', 'error', 'notice', 'warning', 'help'].indexOf(type) >
            -1) {
            return ['ok'];
        }
        return ['cancel', 'ok'];
    }, [footerActionsProp, type]);
    const iconName = useMemo(() => MESSAGE_TYPE[type] || '', [type]);
    const clsPrefix = `${prefix}dialog`;
    const closable = closeMode.includes('close');
    const clses = useMemo(() => ({
        dialog: classNames(`${clsPrefix}`, className, {
            [`${clsPrefix}--centered`]: centered,
        }),
        header: classNames(`${clsPrefix}-header`, {
            [`${clsPrefix}-header--centered`]: centered,
        }),
        closeWrapper: classNames(`${clsPrefix}-close-wrapper`, {
            [`${clsPrefix}-close-wrapper--content`]: !titleProp && closable,
            [`${clsPrefix}-close-wrapper--centered`]: centered && closable,
        }),
        close: classNames(`${clsPrefix}-close`, {
            [`${clsPrefix}-close--centered`]: centered,
        }),
        container: classNames(`${clsPrefix}-container`, {
            [`${clsPrefix}-container--centered ${clsPrefix}-container--${platform}`]: centered,
        }),
        icon: classNames({
            [`${clsPrefix}-icon`]: true,
            [`${clsPrefix}-icon--${type}`]: true,
            [`${clsPrefix}-icon--centered`]: centered,
        }),
        title: classNames(`${clsPrefix}-title`, {
            [`${clsPrefix}-title--centered`]: centered,
            [`${clsPrefix}-title--${platform}`]: platform && centered,
            [`${clsPrefix}-title--fullwidth`]: titleProp && closable,
        }),
        content: classNames(contentClassName, {
            [`${clsPrefix}-content`]: !centered,
            [`${clsPrefix}-content--centered ${clsPrefix}-content--centered-${platform}`]: centered,
        }),
        innerContent: classNames(`${clsPrefix}-inner-content`, {
            [`${clsPrefix}-inner-content--fullwidth`]: !closable || (closable && titleProp),
            [`${clsPrefix}-inner-content--centered`]: centered,
        }),
        footer: classNames({
            [`${clsPrefix}-footer`]: !centered,
            [`${clsPrefix}-footer--centered ${clsPrefix}-footer--centered-${platform}`]: centered,
        }),
    }), [clsPrefix, className, centered, closable, titleProp, platform]);
    const okBtnText = okProps.text ||
        okProps.children ||
        $i18n.get({ id: 'Confirmation', dm: '确定', ns: 'CnDialog' });
    const cancelText = cancelProps.text ||
        cancelProps.children ||
        $i18n.get({ id: 'Cancel', dm: '取消', ns: 'CnDialog' });
    const renderInnerContent = () => {
        let innerContent = content;
        if (isString(content)) {
            innerContent = (React.createElement("span", { className: `${clsPrefix}-content-text` }, content));
        }
        else if (children) {
            innerContent = children;
        }
        if (bgImg) {
            return (React.createElement("div", { className: `${clsPrefix}-content-bgImgWrapper` },
                React.createElement("img", { className: `${clsPrefix}-content-bgImg`, src: bgImg }),
                React.createElement("div", { className: `${clsPrefix}-content-innerWrapper` }, innerContent)));
        }
        else {
            return innerContent;
        }
    };
    const renderTitle = () => {
        let title = titleProp || null;
        if (title) {
            title = isString(title) ? (React.createElement("span", { className: clses.title, style: {
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    lineClamp: '3',
                } }, title)) : (React.createElement("div", { className: clses.title }, title));
        }
        return title;
    };
    const renderFooter = () => {
        const wrapperClassName = classNames(`${clsPrefix}-btn-col`, `${clsPrefix}-btn-col--${platform}`);
        const emptyFooter = null;
        if (footer) {
            if (!isBoolean(footer)) {
                return React.createElement("div", { className: clses.footer }, footer);
            }
            else if (isValidArray(footerActions)) {
                return (React.createElement("div", { className: [clses.footer, `footer-${footerActions.length}`].join(' ') }, footerActions.map((actionType, index) => {
                    let buttonProps = okProps;
                    if (typeof actionType === 'object') {
                        buttonProps = actionType;
                    }
                    else if (actionType === 'cancel') {
                        buttonProps = cancelProps;
                    }
                    const { text, className: btnClassNameProp, model, onClick: _onClick, ...restProps } = buttonProps;
                    const isFirst = index === 0;
                    const isCancel = actionType === 'cancel';
                    const btnClassName = classNames(btnClassNameProp, {
                        [`${clsPrefix}-btn--item`]: true,
                        [`${clsPrefix}-btn`]: !centered,
                        [`${clsPrefix}-btn--centered`]: centered,
                        [`${clsPrefix}-btn--centered-${actionType}`]: centered,
                    });
                    let btnText;
                    if (text) {
                        btnText = text;
                    }
                    else if (isCancel) {
                        btnText = cancelText;
                    }
                    else {
                        btnText = okBtnText;
                    }
                    return (React.createElement(FooterWrapper, { key: `mt_dialog_${index}`, centered: centered, className: centered ? wrapperClassName : '' },
                        !isFirst ? (React.createElement("div", { className: `${clsPrefix}-footer-gap` })) : null,
                        React.createElement(CnButton, { key: `mt_dialog_${index}`, type: isCancel || text ? 'normal' : 'primary', size: size, ...restProps, className: btnClassName, onClick: async (e) => {
                                // 自定义按钮
                                if (isString(actionType)) {
                                    // 简单按钮
                                    if (isCancel) {
                                        onCancel(e);
                                    }
                                    else {
                                        await onOk(e);
                                    }
                                    if (closeMode.includes(actionType)) {
                                        onClose(actionType, e);
                                    }
                                }
                                else if (actionType.onClick) {
                                    actionType.onClick(e);
                                }
                            } }, btnText)));
                })));
            }
        }
        return emptyFooter;
    };
    const titleEl = renderTitle();
    const showHeader = titleEl || (!centered && iconName) || (closable && titleProp);
    return (React.createElement(CnModal, { ...others, ref: ref, bgImg: bgImg, visible: visible, maskClosable: closeMode.includes('mask'), placement: 'center', containerClassName: clses.dialog, onClose: onClose },
        React.createElement("div", { className: clses.container },
            showIcon && centered && iconName ? (React.createElement(CnIcon, { type: `icon-${iconName}`, className: clses.icon, size: "xxl" })) : null,
            React.createElement("div", { className: `${clsPrefix}-main` },
                showHeader ? (React.createElement("div", { className: clses.header },
                    showIcon && !centered && iconName ? (React.createElement(CnIcon, { type: `icon-${iconName}`, className: clses.icon, size: "small" })) : null,
                    titleEl)) : null,
                React.createElement("div", { className: clses.content },
                    React.createElement("div", { className: clses.innerContent }, renderInnerContent())))),
        renderFooter(),
        showBottomCloseIcon ? (React.createElement(CnIcon, { type: "error-r", className: `${clsPrefix}-bottom-close-icon`, size: "xxl", onClick: () => onClose('') })) : null));
};
const CnDialog = withI18n(forwardRef(CnDialogBase), {
    locale,
    componentName: 'CnDialog',
    forwardRef: true,
    _noHoc: true,
});
CnDialog.displayName = 'CnDialog';
export { CnDialog };
